import { useMemo } from 'react'
import { useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useClub } from '../club/ClubProvider.tsx'
import { NOTICE_KIND_KEY, noticeAreasText, noticeDatesText, noticesFor, startTimeText, useNotices } from '../data/courseStatus.ts'
import { useAreas, type AreaRow } from '../data/geo.ts'
import { longDay } from '../data/planUi.ts'
import { localDate } from '../lib/time.ts'
import { BackLink } from '../ui/components.tsx'

const ISO = /^\d{4}-\d{2}-\d{2}$/

/** "État du parcours" (M16-B1): one page for the reception desk, the notices in force on a day (`?d=`, today by default): the kind, the areas by name
 *  and unit, the text in both languages when both exist, the dates, a competition's first start. No name, no machine: nothing on it is about the
 *  team. Built from the device's own copy, so it prints with no network; the daily sheet's print pattern (A4, 8-mm margins, black on white:
 *  `.plan-sheet` in index.css). */
export function CourseStatusPrintPage() {
  const { t, i18n } = useTranslation()
  const { org, site } = useClub()
  const lang = i18n.language
  const search = useSearch({ strict: false }) as { d?: string }
  const date = search.d && ISO.test(search.d) ? search.d : localDate(new Date(), site.timezone)
  const notices = useNotices(); const areas = useAreas()
  const areaById = useMemo(() => new Map((areas ?? []).map((a) => [a.id, a as AreaRow])), [areas])
  if (!notices || !areas) return <p>{t('common.loading')}</p>
  const rows = noticesFor(notices, date)
  const texts = (n: (typeof rows)[number]): string[] => {
    const first = lang.startsWith('en') ? n.text.en : n.text.fr, second = lang.startsWith('en') ? n.text.fr : n.text.en
    return [first, second].flatMap((s) => (s?.trim() ? [s.trim()] : []))
  }
  return (
    <section className="plan-sheet flex flex-col gap-4 print:gap-2 print:text-[11pt] print:leading-[1.25]" data-testid="cs-sheet" data-lines={rows.length}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between print:hidden">
        {/* the route is declared in router.tsx by Z's close (docs/m16-requests/b1.md): the cast goes the day it is */}
        <BackLink to="/settings/course-status" testId="cs-sheet-back" className="sm:w-auto">{t('courseStatus.sheet.back')}</BackLink>
        <button className="btn btn-primary" onClick={() => window.print()} data-testid="cs-sheet-print">{t('plan.printNow')}</button>
      </div>
      <header className="flex flex-col gap-0.5 border-b-2 border-slate-900 pb-2 dark:border-slate-100 print:border-black print:pb-1">
        <p className="text-sm print:text-[10pt]">{org.org_name} · {site.name}</p>
        <h1 className="text-2xl font-bold first-letter:uppercase print:text-[16pt]">{t('courseStatus.sheet.title')} — {longDay(date, lang)}</h1>
        <p className="meta print:text-[10pt] print:text-black">{t('courseStatus.sheet.printedFor', { day: longDay(date, lang) })}</p>
      </header>
      {rows.length === 0 && <p className="text-lg" data-testid="cs-sheet-empty">{t('courseStatus.sheet.none')}</p>}
      {/* M16a review: under sm one card per notice (a four-column percentage table broke every date into a staircase); the table from sm up and on paper */}
      {rows.length > 0 && (
        <ul className="flex flex-col gap-2 sm:hidden print:hidden" data-testid="cs-sheet-cards">
          {rows.map((n) => (
            <li key={n.id} className="card flex flex-col gap-1" data-testid="cs-sheet-card" data-kind={n.kind} data-notice-id={n.id}>
              <p className="font-semibold">{t(NOTICE_KIND_KEY[n.kind])} · {noticeAreasText(n.area_ids, areaById, lang, t)}</p>
              {texts(n).map((s, i) => <p key={i} className={i > 0 ? 'meta' : ''}>{s}</p>)}
              <p className="meta">{noticeDatesText(n, lang, t)}{n.start_time ? ` · ${t('courseStatus.startAt', { time: startTimeText(n.start_time, lang) })}` : ''}</p>
            </li>
          ))}
        </ul>
      )}
      {rows.length > 0 && (
        <table className="hidden w-full border-collapse text-left text-base sm:table print:table print:text-[11pt]" data-testid="cs-sheet-table">
          <thead>
            <tr className="border-b-2 border-slate-900 dark:border-slate-100 print:border-black">
              <th className="w-[18%] p-2 print:py-1">{t('courseStatus.sheet.kind')}</th>
              <th className="w-[24%] p-2 print:py-1">{t('courseStatus.sheet.areas')}</th>
              <th className="p-2 print:py-1">{t('courseStatus.sheet.text')}</th>
              <th className="w-[22%] p-2 print:py-1">{t('courseStatus.sheet.dates')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((n) => (
              <tr key={n.id} className="break-inside-avoid border-b border-slate-300 align-top dark:border-slate-600 print:border-black" data-testid="cs-sheet-row" data-kind={n.kind} data-notice-id={n.id}>
                <td className="p-2 font-semibold print:py-1">{t(NOTICE_KIND_KEY[n.kind])}</td>
                <td className="p-2 print:py-1">{noticeAreasText(n.area_ids, areaById, lang, t)}</td>
                <td className="p-2 print:py-1">
                  {texts(n).map((s, i) => <p key={i} className={i > 0 ? 'meta print:text-[10pt] print:text-black' : ''}>{s}</p>)}
                </td>
                <td className="p-2 print:py-1">
                  <p>{noticeDatesText(n, lang, t)}</p>
                  {n.start_time && <p className="font-semibold">{t('courseStatus.startAt', { time: startTimeText(n.start_time, lang) })}</p>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="meta print:text-[9pt] print:text-black">{t('courseStatus.sheet.footer')}</p>
    </section>
  )
}
